import { Link, type ErrorComponentProps } from '@tanstack/react-router';
import { Button } from '@/components/ui/button';
import { ErrorState, ErrorShell } from '@/components/error-state';
import { classify } from '@/lib/failure';

// Screens for a view that could not be drawn. Several rather than one because a
// router error component cannot vary itself by route and the right exit differs
// by surface. Nothing here imports operator code: these are what a calling
// surface renders.

// No page strip: whoever is here is on a call, and the links are an invitation
// away from it.
export function CallSurfaceError({ error }: ErrorComponentProps) {
  return (
    <ErrorShell>
      <ErrorState failure={classify(error)} size="page">
        <Button variant="ghost" size="sm" asChild>
          <Link to="/">Back to pools</Link>
        </Button>
      </ErrorState>
    </ErrorShell>
  );
}

// The last resort. Reaching it means the frame itself could not render, so it
// assumes nothing about what else works.
export function AppError({ error }: ErrorComponentProps) {
  return (
    <ErrorShell>
      <ErrorState failure={classify(error)} size="page">
        {/* Unconditional, unlike the classified action beside it: a refusal
            offers no action, and this screen has nothing beneath it, so without
            a link of its own it can render as a sentence and no way out. */}
        <Button variant="ghost" size="sm" asChild>
          <Link to="/">Go to the cockpit</Link>
        </Button>
      </ErrorState>
    </ErrorShell>
  );
}

// Not an error: nothing failed, the address is wrong, and dressing it as a
// fault sends people to support.
export function RouteNotFound() {
  return (
    <ErrorShell>
      <div className="rounded-lg border p-6 text-sm">
        <p className="font-medium">There is nothing at this address.</p>
        <p className="text-muted-foreground mt-1">
          The link may be out of date, or the page may have moved.
        </p>
        <div className="mt-4 flex flex-wrap gap-2">
          <Button variant="outline" size="sm" asChild>
            <Link to="/">Go to the cockpit</Link>
          </Button>
          <Button variant="ghost" size="sm" asChild>
            <Link to="/console">Go to the board</Link>
          </Button>
        </div>
      </div>
    </ErrorShell>
  );
}
